import { Navigate, Route, Routes } from "react-router-dom";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { AdminMfaGate } from "@/components/auth/AdminMfaGate";
import { useAuthInitialization } from "@/hooks/useAuthInitialization";

import { LoginPage } from "@/pages/auth/LoginPage";
import { SecurityPage } from "@/pages/settings/SecurityPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

import { AdminDashboardPage } from "@/pages/admin/AdminDashboardPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { AdminUserDetailPage } from "@/pages/admin/AdminUserDetailPage";
import { AdminContentPage } from "@/pages/admin/AdminContentPage";
import { AdminExplorePage } from "@/pages/admin/AdminExplorePage";
import { AdminExploreCollectionPage } from "@/pages/admin/AdminExploreCollectionPage";
import { AdminTrendsPage } from "@/pages/admin/AdminTrendsPage";
import { AdminAIUsagePage } from "@/pages/admin/AdminAIUsagePage";
import { AdminOnboardingPage } from "@/pages/admin/AdminOnboardingPage";
import { AdminAcquisitionPage } from "@/pages/admin/AdminAcquisitionPage";
import { AdminBillingPage } from "@/pages/admin/AdminBillingPage";
import { AdminAuditPage } from "@/pages/admin/AdminAuditPage";
import { AdminLogsPage } from "@/pages/admin/AdminLogsPage";
import { AdminFeedbackPage } from "@/pages/admin/AdminFeedbackPage";
import { AdminHelpQuestionsPage } from "@/pages/admin/AdminHelpQuestionsPage";
import { AdminReportsPage } from "@/pages/admin/AdminReportsPage";
import { AdminAlertsPage } from "@/pages/admin/AdminAlertsPage";
import { AdminInterviewsPage } from "@/pages/admin/AdminInterviewsPage";
import { AdminInterviewDetailPage } from "@/pages/admin/AdminInterviewDetailPage";

/**
 * Admin app routes — the same pages packages/frontend used to serve under
 * "/app/admin/*", now at this app's own root. AdminGuard is a client-side
 * UX affordance only (see that file's comment) — the real access control is
 * server-side on every /admin/* endpoint, plus the TOTP challenge on sign-in
 * (#163).
 */
export function AppRouter() {
  useAuthInitialization();

  return (
    <Routes>
      {/* ── Public: login ── */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* ── Protected: the console itself ── */}
      <Route
        element={
          <ProtectedRoute>
            <AdminGuard>
              <AdminMfaGate>
                <AdminLayout />
              </AdminMfaGate>
            </AdminGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="users/:userId" element={<AdminUserDetailPage />} />
        <Route path="content" element={<AdminContentPage />} />
        <Route path="explore" element={<AdminExplorePage />}>
          <Route index element={<Navigate to="collection" replace />} />
          <Route path="collection" element={<AdminExploreCollectionPage />} />
          <Route path="stored-trends" element={<AdminTrendsPage />} />
        </Route>
        <Route
          path="trends"
          element={<Navigate to="/explore/stored-trends" replace />}
        />
        <Route path="feedback" element={<AdminFeedbackPage />} />
        <Route path="reports" element={<AdminReportsPage />} />
        <Route path="help-questions" element={<AdminHelpQuestionsPage />} />
        <Route path="alerts" element={<AdminAlertsPage />} />
        <Route path="ai-usage" element={<AdminAIUsagePage />} />
        <Route path="onboarding" element={<AdminOnboardingPage />} />
        <Route path="acquisition" element={<AdminAcquisitionPage />} />
        <Route path="billing" element={<AdminBillingPage />} />
        <Route path="audit" element={<AdminAuditPage />} />
        <Route path="logs" element={<AdminLogsPage />} />
        <Route path="internal-interviews" element={<AdminInterviewsPage />} />
        <Route
          path="internal-interviews/:memberId"
          element={<AdminInterviewDetailPage />}
        />

        {/*
          Only Security is carried over from the frontend's settings area —
          Account/Preferences/Connections are user-app concepts (persona,
          subscription tier, publishing connections) that don't exist for an
          admin account. This is where an admin enrols in TOTP MFA (#163).
        */}
        <Route path="settings/security" element={<SecurityPage />} />
      </Route>

      {/* ── Fallback ── */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
